export type PhoneCountryId = 'SG' | 'MY' | 'CN' | 'ID' | 'US';

export type PhoneCountry = {
  id: PhoneCountryId;
  dial: string;
  name: string;
  label: string;
  placeholder: string;
};

export const PHONE_COUNTRIES: PhoneCountry[] = [
  { id: 'SG', dial: '65', name: 'Singapore', label: 'SG (+65)', placeholder: '9123 4567' },
  { id: 'MY', dial: '60', name: 'Malaysia', label: 'MY (+60)', placeholder: '11 3185 9590' },
  { id: 'CN', dial: '86', name: 'China', label: 'CN (+86)', placeholder: '138 0013 8000' },
  { id: 'ID', dial: '62', name: 'Indonesia', label: 'ID (+62)', placeholder: '812 3456 7890' },
  { id: 'US', dial: '1', name: 'United States', label: 'US (+1)', placeholder: '415 555 2671' },
];

export function phoneCountrySelectLabel(item: PhoneCountry): string {
  return `${item.id} +${item.dial}`;
}

const COUNTRIES_BY_DIAL = [...PHONE_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

export function phoneCountryById(id: PhoneCountryId): PhoneCountry {
  return PHONE_COUNTRIES.find((c) => c.id === id) ?? PHONE_COUNTRIES[0]!;
}

export function parseE164Phone(value: string, fallbackCountry: PhoneCountryId = 'SG'): {
  countryId: PhoneCountryId;
  nationalDigits: string;
} {
  const trimmed = value.trim();
  if (!trimmed) return { countryId: fallbackCountry, nationalDigits: '' };

  const digits = trimmed.replace(/\D/g, '');
  if (!digits) return { countryId: fallbackCountry, nationalDigits: '' };

  for (const country of COUNTRIES_BY_DIAL) {
    if (digits.startsWith(country.dial) && digits.length > country.dial.length) {
      return {
        countryId: country.id,
        nationalDigits: digits.slice(country.dial.length),
      };
    }
  }

  if (digits.length === 8) {
    return { countryId: 'SG', nationalDigits: digits };
  }
  if (digits.length === 9 && digits.startsWith('0')) {
    return { countryId: 'SG', nationalDigits: digits.slice(1) };
  }

  return { countryId: fallbackCountry, nationalDigits: digits };
}

export function limitNationalDigits(countryId: PhoneCountryId, digits: string): string {
  const only = digits.replace(/\D/g, '');
  switch (countryId) {
    case 'SG':
      return only.slice(0, 8);
    case 'MY':
      return (only.startsWith('0') ? only.slice(1) : only).slice(0, 10);
    case 'CN':
      return only.slice(0, 11);
    case 'ID':
      return (only.startsWith('0') ? only.slice(1) : only).slice(0, 12);
    case 'US':
      return only.slice(0, 10);
    default:
      return only.slice(0, 15);
  }
}

export function formatNationalPhoneDisplay(countryId: PhoneCountryId, nationalDigits: string): string {
  const digits = limitNationalDigits(countryId, nationalDigits);
  if (!digits) return '';

  if (countryId === 'SG') {
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  }

  if (countryId === 'MY') {
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
  }

  if (countryId === 'CN') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
  }

  if (countryId === 'US') {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  if (digits.length <= 3) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;
}

export function nationalToE164(countryId: PhoneCountryId, nationalDigits: string): string {
  const country = phoneCountryById(countryId);
  let digits = limitNationalDigits(countryId, nationalDigits);
  if (!digits) return '';
  if (countryId === 'MY' || countryId === 'ID') {
    digits = digits.replace(/^0+/, '');
  }
  return `+${country.dial}${digits}`;
}

/** Normalize to E.164 for Lalamove / delivery contacts. */
export function normalizeDeliveryPhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  if (/^\+[1-9]\d{7,14}$/.test(trimmed)) return trimmed;

  const parsed = parseE164Phone(trimmed);
  const e164 = nationalToE164(parsed.countryId, parsed.nationalDigits);
  if (!e164 || !/^\+[1-9]\d{7,14}$/.test(e164)) return null;

  if (parsed.countryId === 'SG' && parsed.nationalDigits.length !== 8) return null;
  if (parsed.countryId === 'MY' && (parsed.nationalDigits.length < 9 || parsed.nationalDigits.length > 10)) {
    return null;
  }

  return e164;
}

export function formatDeliveryPhoneError(label: string): string {
  return `${label} must be a valid phone number.`;
}

/** Normalize CRM / account phone values to E.164 for storage. */
export function normalizeStoredPhone(
  raw?: string | null,
  fallbackCountry: PhoneCountryId = 'SG',
): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return '';
  if (/^\+[1-9]\d{7,14}$/.test(trimmed)) return trimmed;
  const parsed = parseE164Phone(trimmed, fallbackCountry);
  return nationalToE164(parsed.countryId, parsed.nationalDigits) || trimmed;
}

/** Value for IntlPhoneField — accepts legacy local numbers like 91234567. */
export function phoneInputValue(raw?: string | null, fallbackCountry: PhoneCountryId = 'SG'): string {
  return normalizeStoredPhone(raw, fallbackCountry);
}

/** Display phone with country code and spacing, e.g. +65 9123 4567. */
export function formatPhoneDisplay(raw?: string | null, fallbackCountry: PhoneCountryId = 'SG'): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return '';
  const parsed = parseE164Phone(trimmed, fallbackCountry);
  const country = phoneCountryById(parsed.countryId);
  const national = formatNationalPhoneDisplay(parsed.countryId, parsed.nationalDigits);
  if (!national) return trimmed;
  return `+${country.dial} ${national}`;
}

export function phoneTelHref(raw?: string | null, fallbackCountry: PhoneCountryId = 'SG'): string {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return '';
  const normalized = normalizeStoredPhone(trimmed, fallbackCountry) || trimmed;
  const digits = normalized.replace(/[^\d+]/g, '');
  return digits ? `tel:${digits}` : `tel:${trimmed}`;
}
